import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';

import { useLoadableAction } from '../../../hooks/useLoadableAction';
import { TranscriptContextValue } from '../../../store/transcript/store';
import { OcChatConfig, PreviewState } from '../../../types/oc';
import { DataLoadable, isLoadSuccess, loadSuccess, notStarted } from '../../../utils/loadable';
import { MESSAGE_ROLE } from '../../Message/types/messages';
import { getChatHistory } from '../services/ocEndpoints';
import { ChatHistoryItem } from '../types/apiTypes';
import { deleteItem, loadSessionKey, saveLastActivityTime, usePersistSession } from './usePersistSession';
// Session persistence keys namespaced by originToolId to prevent chat leakage
// between different widget instances (e.g. main site vs mobile/single-page) on the same domain.
const getSessionKey = (originToolId: string) => `global-ai-fab-search-chat-${originToolId}`;
const getLastActivityKey = (originToolId: string) => `global-ai-fab-search-chat-last-activity-time-${originToolId}`;
const getUserKey = (originToolId: string) => `global-ai-fab-search-chat-user-${originToolId}`;

/**
 * Detects when a different user has logged in and clears the previous user's
 * session data to prevent cross-user chat leakage on shared browsers.
 * Stores the current userId so the check works on the next page load.
 */
const clearSessionIfUserChanged = (
    userKey: string,
    sessionKey: string,
    lastActivityKey: string,
    userId: string,
): void => {
    const storedUserId = window.localStorage.getItem(userKey);
    if (storedUserId && storedUserId !== userId) {
        window.localStorage.removeItem(sessionKey);
        window.localStorage.removeItem(lastActivityKey);
    }
    window.localStorage.setItem(userKey, userId);
};

export type SessionManager = {
    /**
     * The current session key being used
     */
    initialSessionKey: string | null | undefined;

    /**
     * The current active session ID (reactive)
     */
    currentSessionId: string | null | undefined;

    /**
     * The chat context ref used by TranscriptContextProvider
     */
    chatContextRef: MutableRefObject<TranscriptContextValue | null>;

    /**
     * Whether chat history should be shown
     */
    showChatHistory: boolean;

    /**
     * The chat history loadable (includes loading/error state and data)
     */
    historyLoadable: DataLoadable<ChatHistoryItem[]>;

    /**
     * Clears the current chat session and starts a new one
     */
    resetSession: () => void;

    /**
     * Load a specific session by key
     * @param sessionKey - The session key to restore
     */
    loadSession: (sessionKey: string) => void;

    /**
     * Callback to rename "New chat" sessions with first user message
     * @param sessionKey - The session key to update
     * @param firstMessage - The user's message text
     */
    renameSessionIfNeeded: (sessionKey: string, firstMessage: string) => void;
};

/**
 * Preview session manager hook for preview mode
 * Returns a session manager that uses mocked data instead of real API calls
 */
export const usePreviewSessionManager = (
    showChatHistory: boolean,
    previewState: PreviewState,
): SessionManager => {
    const sessionId = previewState.sessionId || 'preview-session-id';

    // Use mocked session ID as initial session
    const initialSessionKey = sessionId;
    const [currentSessionId, setCurrentSessionId] = useState<string | null | undefined>(initialSessionKey);

    // Create mocked history loadable from previewState
    const historyLoadable: DataLoadable<ChatHistoryItem[]> = useMemo(() => {
        if (!showChatHistory || !previewState.sessionHistory) {
            return notStarted();
        }
        return loadSuccess(previewState.sessionHistory);
    }, [showChatHistory, previewState.sessionHistory]);

    // Create a mock chatContextRef that doesn't persist to localStorage
    // Wrap in useMemo to prevent dependency issues
    const chatContextRef: MutableRefObject<TranscriptContextValue | null> = useMemo(
        () => ({ current: null }),
        []
    );

    const resetSession = useCallback(() => {
        chatContextRef.current?.resetSession?.();
        setCurrentSessionId(sessionId);
    }, [chatContextRef, sessionId]);

    const loadSession = useCallback(
        (sessionKey: string) => {
            if (chatContextRef.current?.loadSession) {
                setCurrentSessionId(sessionKey);
                chatContextRef.current.loadSession(sessionKey).catch(() => {
                    // Silently fail in preview mode
                });
            }
        },
        [chatContextRef]
    );

    const renameSessionIfNeeded = useCallback(() => {
        // No-op in preview mode
    }, []);

    return {
        initialSessionKey,
        currentSessionId,
        chatContextRef,
        showChatHistory,
        historyLoadable,
        resetSession,
        loadSession,
        renameSessionIfNeeded,
    };
};

/**
 * Custom hook to manage AI chat sessions with persistence
 * @param showChatHistory - Whether chat history should be shown and loaded (from tenant config)
 * @param config - Config with isPrivate, isAnonymous from Crest. Only mounted when privacy has succeeded (OcFabWidget).
 * @param originToolId - The origin tool ID for filtering chat history
 * @param isAuthenticated - Whether the user is authenticated (chat history only loads when authenticated)
 * @param isPublicAnonymousUser - Anonymous public visitor using widget IAM (no server history)
 * @param userId - The authenticated user's ID, used to detect user changes and prevent cross-user session leakage
 * @returns SessionManager object with session management methods and state
 */
export const useSessionManager = (
    showChatHistory: boolean,
    config: OcChatConfig,
    originToolId: string,
    isAuthenticated: boolean,
    isPublicAnonymousUser: boolean,
    userId: string,
): SessionManager => {
    const disablePersistence =
        config.isPrivate === true || config.isAnonymous === true || isPublicAnonymousUser;
    const effectiveShowChatHistory = !disablePersistence && showChatHistory;
    const sessionKey = getSessionKey(originToolId);
    const lastActivityKey = getLastActivityKey(originToolId);
    const userKey = getUserKey(originToolId);
    if (!disablePersistence && isAuthenticated && userId) {
        clearSessionIfUserChanged(userKey, sessionKey, lastActivityKey, userId);
    }
    const initialSessionKey = disablePersistence ? null : loadSessionKey(sessionKey, lastActivityKey);

    // Track current session ID in state so it's reactive (initialize with existing session)
    const [currentSessionId, setCurrentSessionId] = useState<string | null | undefined>(initialSessionKey);

    // Only load history data if chat history should be shown
    const [serverHistoryLoadable, loadHistoryData] = useLoadableAction(getChatHistory);
    const [historyItems, setHistoryItems] = useState<ChatHistoryItem[]>([]);

    // Callback for when new sessions are created (called from usePersistSession)
    const handleNewSession = useCallback(
        (sessionId: string) => {
            // Update current session ID state
            setCurrentSessionId(sessionId);

            if (!effectiveShowChatHistory) {
                return;
            }

            // Check if session already exists in local or server history
            setHistoryItems((prev) => {
                // A sessionId may be '' temporarily while loading a new session
                // If we have a real sessionId and there's a temporary empty one, update it
                if (sessionId !== '') {
                    const tempIndex = prev.findIndex((item) => item.ChatSessionId === '');
                    if (tempIndex !== -1) {
                        // Update the temporary entry with the real session ID
                        const updated = [...prev];
                        updated[tempIndex] = {
                            ...updated[tempIndex],
                            ChatSessionId: sessionId
                        };
                        return updated;
                    }
                }

                // Check local items
                const existsInLocal = prev.some((item) => item.ChatSessionId === sessionId);
                if (existsInLocal) {
                    return prev;
                }

                // Check server data (if loaded)
                if (isLoadSuccess(serverHistoryLoadable)) {
                    const existsInServer = serverHistoryLoadable.data?.some((item) => item.ChatSessionId === sessionId);
                    if (existsInServer) {
                        return prev; // Already in server history, no need to add locally
                    }
                }

                // Add new chat item to local state
                const newHistoryItem: ChatHistoryItem = {
                    ChatSessionId: sessionId,
                    // `Label: null` indicates a new unlabeled session; the label will be set when the first message is sent.
                    Label: null,
                    CreatedOn: new Date().toISOString(),
                    OriginTool: originToolId
                };

                return [newHistoryItem, ...prev];
            });
        },
        [effectiveShowChatHistory, serverHistoryLoadable, originToolId]
    );

    const chatContextRef = usePersistSession(
        sessionKey,
        lastActivityKey,
        handleNewSession,
        disablePersistence
    );

    useEffect(() => {
        // Only load chat history if history should be shown AND user is authenticated (not public anonymous)
        if (effectiveShowChatHistory && isAuthenticated && !isPublicAnonymousUser) {
            loadHistoryData(config, originToolId);
        }
    }, [effectiveShowChatHistory, isAuthenticated, isPublicAnonymousUser, config, originToolId, loadHistoryData]);

    // Merge server history into local state once it loads
    useEffect(() => {
        if (!effectiveShowChatHistory || !isLoadSuccess(serverHistoryLoadable) || !serverHistoryLoadable.data) {
            return;
        }

        setHistoryItems((prev) => {
            // Get session IDs that are already in local state
            const localSessionIds = new Set(prev.map((item) => item.ChatSessionId));

            // Add server items that aren't already in local state
            const serverItemsToAdd = serverHistoryLoadable.data.filter(
                (item) => !localSessionIds.has(item.ChatSessionId)
            );

            const mergedItems = [...prev, ...serverItemsToAdd];

            // Check if initial session from localStorage exists in the merged list
            // If not, add it (this handles sessions with no messages that aren't returned by backend)
            if (initialSessionKey) {
                const initialSessionExists = mergedItems.some((item) => item.ChatSessionId === initialSessionKey);

                if (!initialSessionExists) {
                    const currentMessages = chatContextRef.current?.state.messages || [];
                    const firstUserMessage = currentMessages.find((msg) => msg.role === MESSAGE_ROLE.USER && !!msg.message.trim());

                    const newHistoryItem: ChatHistoryItem = {
                        ChatSessionId: initialSessionKey,
                        // Use first user message as label, or null for "New chat"
                        Label: firstUserMessage ? firstUserMessage.message.trim() : null,
                        CreatedOn: new Date().toISOString(),
                        OriginTool: originToolId
                    };

                    return [newHistoryItem, ...mergedItems];
                }
            }

            return mergedItems;
        });
    }, [serverHistoryLoadable, effectiveShowChatHistory, initialSessionKey, chatContextRef, originToolId]);

    // Combine server and local history items into a single loadable
    const historyLoadable: DataLoadable<ChatHistoryItem[]> = useMemo(() => {
        if (!isLoadSuccess(serverHistoryLoadable)) {
            return serverHistoryLoadable;
        }

        // All items are now in historyItems (merged)
        return {
            ...serverHistoryLoadable,
            data: historyItems
        };
    }, [serverHistoryLoadable, historyItems]);

    // Callback to rename a session when the first user message is sent
    const renameSessionIfNeeded = useCallback(
        (sessionKey: string, message: string) => {
            if (!effectiveShowChatHistory) {
                return;
            }

            // Use the message as the label (don't update if empty)
            const newLabel = message.trim();
            if (!newLabel) {
                return;
            }

            // Check if this is truly the first user message in the session
            // If there are already multiple messages, the session likely already has a label
            const currentMessages = chatContextRef.current?.state.messages || [];
            const validUserMessageCount = currentMessages.filter((msg) => msg.role === MESSAGE_ROLE.USER && !!msg.message.trim()).length;
            if (validUserMessageCount > 0) {
                return; // Not the first message with content, already has a label
            }

            // Use functional update to access latest historyItems
            setHistoryItems((prev) => {
                const historyItem = prev.find((item) => item.ChatSessionId === sessionKey);
                const needsRename = historyItem?.Label === null;

                if (!needsRename) {
                    return prev;
                }

                // Update the history item
                return prev.map((item) => (item.ChatSessionId === sessionKey ? { ...item, Label: newLabel } : item));
            });
        },
        [effectiveShowChatHistory, chatContextRef]
    );

    const resetSession = useCallback(() => {
        chatContextRef.current?.resetSession?.();

        // Clear the persisted session data from localStorage
        // usePersistSession will update it when the new session is created
        deleteItem(sessionKey);
        deleteItem(lastActivityKey);

        // The new session will be added automatically via the handleNewSession callback
    }, [chatContextRef, sessionKey, lastActivityKey]);

    const loadSession = useCallback(
        (sessionId: string) => {
            if (chatContextRef.current?.loadSession) {
                // Update current session ID immediately for UI responsiveness
                setCurrentSessionId(sessionId);

                chatContextRef.current
                    .loadSession(sessionId)
                    .then(() => {
                        // usePersistSession will update the sessionKey in localStorage
                        // but we want to ensure the timer is reset after a new session is loaded
                        saveLastActivityTime(lastActivityKey);
                    })
                    .catch((error) => {
                        // eslint-disable-next-line no-console
                        console.error('Failed to load session:', error);
                    });
            }
        },
        [chatContextRef, lastActivityKey]
    );

    return {
        initialSessionKey,
        currentSessionId,
        chatContextRef,
        showChatHistory: effectiveShowChatHistory,
        historyLoadable,
        resetSession,
        loadSession,
        renameSessionIfNeeded
    };
};
